import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

// ── Types ──────────────────────────────────────────────────────────────

interface AdminMetrics {
  mrr: number;
  arr: number;
  active_customers: number;
  active_count: number;
  trialing_count: number;
  canceled_count: number;
  churn_rate: number;
  total_customers: number;
  revenue_by_plan: Record<string, number>;
}

interface MrrHistoryPoint { month: string; mrr: number }
interface CustomersHistoryPoint { month: string; active: number; trialing: number }

interface AdminCustomerDiscount { name: string; percent_off?: number; amount_off?: number }

interface AdminCustomer {
  id: string;
  email: string;
  first_name: string;
  plan: string | null;
  plan_name: string | null;
  status: string;
  created: string | null;
  discount: AdminCustomerDiscount | null;
}

interface AiCreditEntry {
  workspace_id: string;
  used: number;
  monthly_limit: number;
  email: string;
}

export interface StripeCoupon {
  id: string;
  name: string;
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  duration: string;
  duration_in_months: number | null;
  times_redeemed: number;
  valid: boolean;
  created: string;
  promotion_code: string | null;
}

export interface PricingPlan {
  plan_key: string;
  label: string;
  price_amount: number;
  price_id: string;
  product_id: string;
  features: string[];
  badge: string | null;
  updated_at: string;
}

interface AdminData {
  metrics: AdminMetrics;
  mrr_history: MrrHistoryPoint[];
  customers_history: CustomersHistoryPoint[];
  customers: AdminCustomer[];
  product_names: Record<string, string>;
  ai_credits?: AiCreditEntry[];
  cached_at?: string;
}

// ── Helper: invoke admin-dashboard edge function ───────────────────────

async function adminInvoke<T = unknown>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-dashboard", { body });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data as T;
}

// ── Query keys ─────────────────────────────────────────────────────────

const ADMIN_KEYS = {
  all: ["admin"] as const,
  role: ["admin", "role"] as const,
  stats: ["admin", "stats"] as const,
};

// ── Hook ───────────────────────────────────────────────────────────────

export function useAdmin() {
  const { user } = useAuth();
  const qc = useQueryClient();

  // 1) Admin role check
  const roleQuery = useQuery({
    queryKey: ADMIN_KEYS.role,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .eq("role", "admin")
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
    staleTime: Infinity,
  });

  const isAdmin = roleQuery.data ?? false;

  // 2) Dashboard stats (auto-fetched when admin)
  const statsQuery = useQuery({
    queryKey: ADMIN_KEYS.stats,
    queryFn: () => adminInvoke<AdminData>({ action: "stats" }),
    enabled: isAdmin,
    staleTime: 5 * 60_000, // 5 min cache
  });

  // Force refresh (bypasses server cache)
  const fetchStats = (force?: boolean) => {
    if (force) {
      return qc.fetchQuery({
        queryKey: ADMIN_KEYS.stats,
        queryFn: () => adminInvoke<AdminData>({ action: "stats", force: true }),
      });
    }
    return qc.invalidateQueries({ queryKey: ADMIN_KEYS.stats });
  };

  // 3) Mutations with auto-invalidation
  const invalidateStats = () => qc.invalidateQueries({ queryKey: ADMIN_KEYS.stats });

  const resetPassword = useMutation({
    mutationFn: (email: string) => adminInvoke({ action: "reset-password", email }),
  });

  const changePlan = useMutation({
    mutationFn: (p: { customerEmail: string; priceId: string }) =>
      adminInvoke({ action: "change-plan", ...p }),
    onSuccess: invalidateStats,
  });

  const setAiLimit = useMutation({
    mutationFn: (p: { workspaceId: string; newLimit: number }) =>
      adminInvoke({ action: "set-ai-limit", ...p }),
    onSuccess: invalidateStats,
  });

  const setAiLimitAll = useMutation({
    mutationFn: (newLimit: number) =>
      adminInvoke({ action: "set-ai-limit-all", newLimit }),
    onSuccess: invalidateStats,
  });

  const fetchHelpContent = () =>
    adminInvoke<{ content: string | null; updated_at: string | null }>({ action: "get-help-content" });

  const updateHelpContent = useMutation({
    mutationFn: (content: string) => adminInvoke({ action: "update-help-content", content }),
  });

  const mergeHelpContent = useMutation({
    mutationFn: (defaultContent: string) =>
      adminInvoke<{ success: boolean; content: string }>({ action: "merge-help-content", defaultContent }),
  });

  const listCoupons = () => adminInvoke<{ coupons: StripeCoupon[] }>({ action: "list-coupons" });

  const createCoupon = useMutation({
    mutationFn: (params: { name: string; percent_off?: number; amount_off?: number; currency?: string; duration?: string; duration_in_months?: number; code?: string }) =>
      adminInvoke({ action: "create-coupon", ...params }),
  });

  const deleteCoupon = useMutation({
    mutationFn: (couponId: string) => adminInvoke({ action: "delete-coupon", couponId }),
  });

  const getPricing = () => adminInvoke<{ plans: PricingPlan[] }>({ action: "get-pricing" });

  const updatePricing = useMutation({
    mutationFn: (params: { plan_key: string; price_amount?: number; label?: string; badge?: string }) =>
      adminInvoke({ action: "update-pricing", ...params }),
  });

  return {
    isAdmin,
    adminLoading: roleQuery.isLoading,
    data: statsQuery.data ?? null,
    dataLoading: statsQuery.isLoading || statsQuery.isFetching,
    error: statsQuery.error?.message ?? null,
    fetchStats,
    // Mutations — expose mutateAsync to keep same API surface
    resetPassword: resetPassword.mutateAsync,
    changePlan: (email: string, priceId: string) => changePlan.mutateAsync({ customerEmail: email, priceId }),
    setAiLimit: (workspaceId: string, newLimit: number) => setAiLimit.mutateAsync({ workspaceId, newLimit }),
    setAiLimitAll: setAiLimitAll.mutateAsync,
    fetchHelpContent,
    updateHelpContent: updateHelpContent.mutateAsync,
    mergeHelpContent: mergeHelpContent.mutateAsync,
    listCoupons,
    createCoupon: createCoupon.mutateAsync,
    deleteCoupon: deleteCoupon.mutateAsync,
    getPricing,
    updatePricing: updatePricing.mutateAsync,
  };
}
